/**
 * Loads converted catalog models and fits them to a placed item.
 *
 * Fitting is the whole job here. A GLB coming out of the Unity FBX carries arbitrary
 * units and an arbitrary origin, so three numbers from the model XML do the work:
 * `scale` (raw mesh → catalog metres), the mesh `bounds` (where its bottom actually is),
 * and `size` (the catalog dimensions the project's own Scale multiplies).
 */
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import type { Item, Opening, RGB, Surface } from '../../core/hp/types';
import { TextureImageCache } from './TextureImageCache';
import { ResourcePool, resourcesOf } from './ResourcePool';
import { TextureVariants, textureVariantsBuilt } from './TextureVariants';
import { bindMaterialSurface, describeMaterial, type MaterialSlotInfo } from './MaterialTransformPreview';
import { restoreModeGeometry } from './modeVisibility';
import { flattenModules } from './assemble';
import { addOpeningCurtains, buildProceduralOpening, openingAssembly } from './OpeningModel';
import { openingDesign } from '../../state/openingProperties';
import { CATALOG_TEXTURE_BASE, emissiveOf, normalTextureOf, metalnessOf, opacityOf, repeatOf, resolveTexture, roughnessOf, specularOf } from '../textures';
import { catalogKeyOf, ensureWebBundles, fetchModels, subscribeWebStates, webStateOf, type CatalogModel, type WebState } from '../../state/catalogApi';
import { builtByCode } from '../../state/catalog';
import { builtinSurfaces } from '../../state/builtinTextures';
import { customTextureUrl } from '../textures';
import { fitScale, noteModelBounds, type ModelBounds } from '../../core/geometry/modelBounds';
import { BORROWED_2D, borrowedQuad, fittedToQuad, parseModelDefinition, pickImage, placedMeasures, slotFor, yawOf, type DefinitionSlot, type ModelDefinition } from './modelDefinition';
import { t, type TranslationKey } from '../../i18n/I18n';

export interface ModelEntry {
  id: string;
  converted: boolean;
  size: [number, number, number];
  scale: number;
  yawQuarters: 0 | 1;
  bounds: { min: [number, number, number]; max: [number, number, number] };
  /** Local XML Position in cm; cloud entries contain the measured metre offset instead. */
  pivot: [number, number, number];
  url: string;
  materials: string[];
  variations: Array<{ id: string; slots: Array<{ for: string; texture?: string; color?: string; tiling?: [number, number] }> }>;
  tags: string[];
  bytes: number;
  /** `catalog` on entries the catalog's web worker converted; absent on a static manifest's. */
  kind?: 'cloud' | 'builtin' | 'catalog';
  /** True when `pivot` is the measured metre offset rather than a local XML Position in cm. */
  measured?: boolean;
  /**
   * The worker's GLB layout. 2: the geometry alone, in the FBX's own frame (`geometry` is its
   * raw bounds), the archive's pictures by name (`images`); the definition is read here and
   * fills size, bounds, pivot, variations and tags. 1 or absent: all of it came baked in.
   */
  format?: number;
  geometry?: { min: [number, number, number]; max: [number, number, number] };
  images?: Record<string, number>;
  definition?: ModelDefinition;
  /** A 2D item drawn on a borrowed quad: the quad's material its picture goes on. */
  flatMaterial?: string;
}

/**
 * Entries read out of the catalog's web bundles, shared by every library instance — the one
 * that parses the project and the viewer's own — so a GLB is fetched once per page. Only a
 * settled answer is kept: a model still in the queue is asked about again next time.
 */
const remoteEntries = new Map<string, ModelEntry | null>();
const remotePending = new Map<string, Promise<ModelEntry | null>>();
const remoteBuffers = new Map<string, ArrayBuffer>();

/** A converted model's bounds, as `instance` stands it, go where the stairwells read them (`modelBounds.ts`). */
function measured(id: string, entry: ModelEntry | null | undefined): ModelEntry | undefined {
  if (entry?.converted && entry.bounds) noteModelBounds(id, modelBoundsOf(entry));
  return entry ?? undefined;
}

/**
 * The bounds `instance` fits to an item: the model as its definition scales and turns it, at the catalog size. A
 * definition's model is measured so when it loads (`placedMeasures`); an older bundle's baked bounds come before its
 * quarter turn, which takes x to z and z to −x.
 */
function modelBoundsOf(entry: ModelEntry): ModelBounds {
  const { min, max } = entry.bounds;
  return entry.yawQuarters ? { size: entry.size, min: [min[2], min[1], -max[0]], max: [max[2], max[1], -min[0]] } : { size: entry.size, min, max };
}

/** Catalog documents by editor id, asked for in batches before a project's models load. */
const documents = new Map<string, CatalogModel | null>();
let textureIndexPromise: Promise<Record<string, string>> | null = null;
/** A web bundle is a static file on the CDN, but a large one on a slow link takes a while. */
const REMOTE_TIMEOUT_MS = 90_000;

/**
 * The id the app names a bundle by: a cloud id as is, a built-in `category/id/id` by its
 * directory name. Kept for what still keys on it; the catalog itself is asked through
 * `catalogKeyOf`.
 */
export function bundleIdOf(id: string): string | null {
  if (/^[0-9a-f]{24}$/i.test(id)) return id.toLowerCase();
  if (id.includes('-region-')) return null;
  const name = id.split('/').pop() ?? '';
  return /^[A-Za-z0-9][A-Za-z0-9_.-]{0,79}$/.test(name) ? name : null;
}

/** The manifest entry a converted GLB carries in `asset.extras.entry`. */
export function entryFromGlb(data: ArrayBuffer): ModelEntry | null {
  if (data.byteLength < 20) return null;
  const view = new DataView(data);
  if (view.getUint32(0, true) !== 0x46546c67 || view.getUint32(16, true) !== 0x4e4f534a) return null;
  const length = view.getUint32(12, true);
  try {
    const json = JSON.parse(new TextDecoder().decode(new Uint8Array(data, 20, length)));
    const entry = json?.asset?.extras?.entry;
    return entry && typeof entry === 'object' ? entry as ModelEntry : null;
  } catch { return null; }
}

/**
 * One picture of a GLB by texture index (the numbers `entry.images` maps names to): its bytes
 * and type, read straight out of the binary chunk. Null when the file has no such picture.
 */
export function glbPicture(data: ArrayBuffer, textureIndex: number): { bytes: Uint8Array; mimeType: string } | null {
  if (data.byteLength < 20) return null;
  const view = new DataView(data);
  if (view.getUint32(0, true) !== 0x46546c67 || view.getUint32(16, true) !== 0x4e4f534a) return null;
  const jsonLength = view.getUint32(12, true), binary = 20 + jsonLength;
  if (binary + 8 > data.byteLength || view.getUint32(binary + 4, true) !== 0x004e4942) return null;
  type Json = { textures?: Array<{ source?: number }>; images?: Array<{ bufferView?: number; mimeType?: string }>; bufferViews?: Array<{ buffer?: number; byteOffset?: number; byteLength: number }> };
  let json: Json;
  try { json = JSON.parse(new TextDecoder().decode(new Uint8Array(data, 20, jsonLength))) as Json; } catch { return null; }
  const source = json.textures?.[textureIndex]?.source;
  const image = source === undefined ? undefined : json.images?.[source];
  const bufferView = image?.bufferView === undefined ? undefined : json.bufferViews?.[image.bufferView];
  if (!image || !bufferView || (bufferView.buffer ?? 0) !== 0) return null;
  const start = binary + 8 + (bufferView.byteOffset ?? 0), end = start + bufferView.byteLength;
  if (end > data.byteLength) return null;
  return { bytes: new Uint8Array(data.slice(start, end)), mimeType: image.mimeType ?? 'image/jpeg' };
}

/** Catalog texture file stem → path under the texture base, for a variation's `Texture="wood-oak"`. */
async function textureIndex(): Promise<Record<string, string>> {
  textureIndexPromise ??= CATALOG_TEXTURE_BASE === null ? Promise.resolve({}) : fetch(`${CATALOG_TEXTURE_BASE}/index.json`)
    .then(async (r) => (r.ok ? r.json() as Promise<Record<string, string>> : {}))
    .catch(() => ({}));
  return textureIndexPromise;
}

/**
 * The surface a definition slot dresses a material with. Only what the converter cannot
 * embed: catalog textures (the renderer owns that library) and colours. A user upload or an
 * archive image is already in the GLB.
 */
export function stockSurfaceFor(slot: { texture?: string; color?: string }, index: Record<string, string>, entry: ModelEntry): Surface | null {
  const texture = slot.texture;
  if (texture && texture !== 'mirror' && !texture.startsWith('[custom]')) {
    const path = texture.replace(/\\/g, '/').replace(/^Assets\/Resources\//, '');
    if (path.startsWith('Textures/')) return { textureKind: 'catalog', texture: path.slice('Textures/'.length) };
    const stem = (path.split('/').pop() ?? '').replace(/\.[^.]+$/, '').toLowerCase();
    const hit = index[stem] ?? index[stem.replace(/^tx_/, '')];
    if (hit) return { textureKind: 'catalog', texture: hit };
    // An archive image is already embedded in a catalog-converted GLB: no override, the
    // GLB's own material stands. The old on-demand API served such images separately.
    if (entry.kind === 'cloud' && /\.(jpe?g|png)$/i.test(path)) {
      return { textureKind: 'modelOwn', textureOwner: entry.id, texture: path.split('/').pop() };
    }
    if (entry.kind === 'catalog' && /\.(jpe?g|png)$/i.test(path)) return null;
  }
  if (slot.color) {
    const hex = slot.color.replace('#', '');
    if (hex.length >= 6) return { color: [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255) as RGB };
  }
  return null;
}

// Drawn at the slot's <Tiling> as it stands, as `shape` and the app's MaterialUtils.CreateMaterial: `surfaceRepeat` would unsign and cap it.
const definitionRepeats = new WeakMap<Surface, [number, number]>();

/**
 * A format-2 model's slot naming a picture `shape` dresses with — one of its archive's or a person's
 * upload — at the repeat `shape` gives it, for a variation other than the one that dressed the model.
 */
function definitionPicture(slot: { texture?: string; tiling?: [number, number] }, entry: ModelEntry): Surface | null {
  const texture = slot.texture ?? '';
  if ((entry.format ?? 1) < 2 || !texture) return null;
  const tiling = slot.tiling ?? [1, 1];
  const tileMeters: [number, number] = [1 / (Math.abs(tiling[0]) || 1), 1 / (Math.abs(tiling[1]) || 1)];
  let surface: Surface;
  if (texture.startsWith('[custom]')) surface = { textureKind: 'custom', customTexture: texture.slice('[custom]'.length), tileMeters };
  else if (/\.(jpe?g|png)$/i.test(texture) && !/[\\/]/.test(texture) && pickImage(entry.images ?? {}, texture) !== undefined) {
    surface = { textureKind: 'modelOwn', textureOwner: entry.id, texture, tileMeters };
  } else return null;
  definitionRepeats.set(surface, tiling);
  return surface;
}

function drawRepeat(surface: Surface, explicitTiling: boolean): [number, number] {
  return definitionRepeats.get(surface) ?? repeatOf(surface, explicitTiling);
}

const sameTiling = (a: [number, number] = [1, 1], b: [number, number] = [1, 1]) => a[0] === b[0] && a[1] === b[1];

export interface ModelLibraryOptions {
  warmCacheBytes?: number;
  timeoutMs?: number;
  loadModel?: (entry: ModelEntry, signal: AbortSignal) => Promise<THREE.Group>;
  fetch?: typeof fetch;
}

export class ModelLibrary {
  private index: Record<string, ModelEntry> | null = null;
  private loading: Promise<Record<string, ModelEntry>> | null = null;
  private cache = new Map<string, Promise<THREE.Group | null>>();
  private loader = new GLTFLoader();
  private textureLoader = new THREE.TextureLoader();
  readonly textureImages: TextureImageCache;
  private readonly resources: ResourcePool;
  private sourceKeys = new Map<string, object>();
  private leases = new WeakMap<THREE.Object3D, () => void>();
  private materials = new Map<string, THREE.Material>();
  private textureVariants?: TextureVariants;
  private sources = new Set<THREE.Object3D>();
  private requests = new Set<AbortController>();
  /** Pictures read out of web GLBs as object URLs, by bundle URL and texture index. */
  private archivePictures = new Map<string, Promise<string | null>>();
  private disposed = false;

  /** Without a manifest URL the library knows only what the catalog converts; tests still hand in a static one. */
  constructor(private readonly manifestUrl: string | null = null, private readonly options: ModelLibraryOptions = {}) {
    this.resources = new ResourcePool(options.warmCacheBytes ?? 32 * 1024 * 1024);
    this.textureImages = new TextureImageCache(this.textureLoader, this.resources);
    this.textureLoader.crossOrigin = 'anonymous';
    // The published models are meshopt-compressed (50 MB of geometry down to 3 MB).
    // Without this decoder every one of them fails to parse and the scene is all boxes.
    this.loader.setMeshoptDecoder(MeshoptDecoder);
  }

  async manifest(): Promise<Record<string, ModelEntry>> {
    if (this.index) return this.index;
    const manifestUrl = this.manifestUrl;
    if (!manifestUrl) { this.index = {}; return this.index; }
    this.loading ??= this.request(manifestUrl, async (signal) => {
      const r = await (this.options.fetch ?? fetch)(manifestUrl, { signal, cache: 'no-cache' });
      if (!r.ok) throw new Error(t('#ViewModelCatalogHttpError', r.status));
      const j: unknown = await r.json();
      if (!j || typeof j !== 'object' || Array.isArray(j) || !Object.keys(j).length) {
        throw new Error(t('#ViewModelCatalogEmpty'));
      }
      return j as Record<string, ModelEntry>;
    }).then((index) => { this.index = index; return index; })
      .catch((error) => { this.loading = null; throw error; });
    return this.loading;
  }

  release(node: THREE.Object3D): void {
    restoreModeGeometry(node);
    node.removeFromParent();
    node.traverse(object => { this.leases.get(object)?.(); this.leases.delete(object); });
    this.resources.disposeLoose(resourcesOf(node));
    this.trimUnused();
  }

  retainResources(root: THREE.Object3D): () => void { return this.resources.retain(resourcesOf(root)); }
  trimUnused(): void { this.resources.trim(); }
  resourceStats() { return { modelSources: this.sources.size, ...this.resources.stats(),
    ...this.textureVariants?.stats(),
    compressedReady: this.textureVariants?.completed ?? 0, compressedFallbacks: this.textureVariants?.fallbacks ?? 0 }; }
  configureRenderer(renderer: THREE.WebGLRenderer): void {
    if (this.textureVariants || this.disposed || !textureVariantsBuilt) return;
    if (typeof location !== 'undefined' && new URLSearchParams(location.search).get('textureVariants') === '0') return;
    this.textureVariants = new TextureVariants(renderer); this.textureImages.setVariants(this.textureVariants);
  }

  dispose(): void {
    this.disposed = true;
    for (const request of this.requests) request.abort();
    this.requests.clear();
    this.textureImages.dispose();
    this.textureVariants?.dispose();
    this.resources.dispose();
    this.sources.clear();
    this.cache.clear();
    this.sourceKeys.clear(); this.materials.clear();
    for (const picture of this.archivePictures.values()) void picture.then((url) => { if (url) URL.revokeObjectURL(url); });
    this.archivePictures.clear();
  }

  private async request<T>(url: string, run: (signal: AbortSignal) => Promise<T>, late?: (value: T) => void, timeoutMs?: number): Promise<T> {
    if (this.disposed) throw new Error(t('#ViewLoadCanceled'));
    const controller = new AbortController();
    this.requests.add(controller);
    return new Promise<T>((resolve, reject) => {
      let settled = false;
      const fail = (message: string) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        this.requests.delete(controller);
        reject(new Error(`${message}: ${url}`));
      };
      const timer = setTimeout(() => { fail(t('#ViewLoadTimedOut')); controller.abort(); }, timeoutMs ?? this.options.timeoutMs ?? 20_000);
      controller.signal.addEventListener('abort', () => fail(t('#ViewLoadCanceled')), { once: true });
      Promise.resolve().then(() => run(controller.signal)).then((value) => {
        if (settled || this.disposed) { late?.(value); return; }
        settled = true;
        clearTimeout(timer);
        this.requests.delete(controller);
        resolve(value);
      }, (error) => fail(error instanceof Error ? error.message : String(error)));
    });
  }

  has(id: string): boolean {
    return !!this.index?.[id]?.converted;
  }

  /** Catalog tags per model id, for `ParseOptions.modelTags`. */
  tagMap(): Map<string, string[]> {
    return new Map(Object.entries(this.index ?? {}).map(([id, e]) => [id, e.tags ?? []]));
  }

  /**
   * Real catalog dimensions, for `ParseOptions.catalogDims`.
   *
   * A cloud model's Width/Height/Depth in the project file are unreliable — pillow sets
   * store millimetres, chandeliers store post-rotation extents — so the measured mesh
   * size wins wherever we have one.
   */
  dimsMap(): Map<string, [number, number, number]> {
    const out = new Map<string, [number, number, number]>();
    for (const [id, e] of Object.entries(this.index ?? {})) {
      if (e.converted && e.size?.[1]) out.set(id, e.size);
    }
    return out;
  }

  /**
   * Cloud mesh-bottom offsets in metres, for `ParseOptions.catalogPivots`.
   *
   * A wall item's stored Y is the height of its PIVOT, not of its bottom. Without this a
   * TV or a framed picture takes the bottom-pivot fallback and hangs at the wrong height.
   */
  pivotMap(): Map<string, number> {
    const out = new Map<string, number>();
    for (const [id, e] of Object.entries(this.index ?? {})) {
      // Lite import_project.py excludes local entries: their XML Position is raw
      // centimetres, not the cloud converter's measured mesh-bottom offset.
      if ((/^[a-f0-9]{24}$/i.test(id) || e.measured) && e.converted && Number.isFinite(e.pivot?.[1])) out.set(id, e.pivot[1]);
    }
    return out;
  }

  entry(id: string): ModelEntry | undefined {
    return this.index?.[id];
  }

  /**
   * The manifest entry for a model: from the static manifest when a test supplies one, else
   * from the catalog's web bundle once the queue reports it `done`. A model still queued or
   * failed has no entry yet — the caller shows a placeholder and `webStateOf` says why.
   */
  async resolve(id: string): Promise<ModelEntry | undefined> {
    const index = await this.manifest();
    const hit = index[id];
    if (hit?.converted) return measured(id, hit);
    const shared = remoteEntries.get(id);
    if (shared !== undefined) {
      if (shared) index[id] = shared;
      return measured(id, shared ?? hit);
    }
    // A static manifest is the whole world for the library that has one (tests, tools):
    // the catalog is asked only when nothing was handed in.
    if (this.manifestUrl || !catalogKeyOf(id)) return hit;
    let pending = remotePending.get(id);
    if (!pending) {
      pending = this.fromCatalog(id).finally(() => remotePending.delete(id));
      remotePending.set(id, pending);
    }
    const entry = await pending;
    if (entry) index[id] = entry;
    return measured(id, entry ?? hit);
  }

  /** The queue's answer for a model, as last heard. */
  webStateOf(id: string): WebState | undefined { return webStateOf(id); }

  /**
   * True once the queue's answer changes for a model it is converting, for `prepare` to be asked again; false at once
   * for a model it is not converting, and as `signal` aborts.
   */
  webStateChange(id: string, signal?: AbortSignal): Promise<boolean> {
    const status = webStateOf(id)?.status;
    if ((status !== 'queued' && status !== 'running') || signal?.aborted) return Promise.resolve(false);
    return new Promise((resolve) => {
      const finish = (changed: boolean) => { stop(); signal?.removeEventListener('abort', abort); resolve(changed); };
      const abort = () => finish(false);
      const stop = subscribeWebStates((modelId) => { if (modelId === id) finish(true); });
      signal?.addEventListener('abort', abort, { once: true });
    });
  }

  /** The catalog's documents for these ids, each asked for once. */
  private async documentsOf(ids: string[]): Promise<Map<string, CatalogModel | null>> {
    const wanted = ids.filter((id) => !documents.has(id) && catalogKeyOf(id));
    if (wanted.length) {
      const answer = await fetchModels(wanted);
      for (const id of wanted) documents.set(id, answer[id] ?? null);
    }
    return new Map(ids.map((id) => [id, documents.get(id) ?? null]));
  }

  private async definitionXml(url: string): Promise<string> {
    return this.request(url, async (signal) => {
      const response = await (this.options.fetch ?? fetch)(url, { signal });
      if (!response.ok) throw new Error(t('#ViewModelDefinitionHttpError', response.status));
      return response.text();
    });
  }

  /**
   * A 2D item — a picture, a sticker, a rug — the way the app draws it: one of the app's own quads
   * (BORROWED_2D), stretched to the item's size, its picture on the quad's picture material. It
   * needs no conversion of its own. Undefined while the quad itself is not converted yet: the
   * item then goes through the queue as before.
   */
  private async flatFromCatalog(id: string, document: CatalogModel): Promise<ModelEntry | undefined> {
    if (typeof document.definition !== 'string') return undefined;
    const definition = parseModelDefinition(await this.definitionXml(document.definition),
      { width: document.width, height: document.height, depth: document.depth, tags: document.tags });
    const quad = borrowedQuad(definition) ?? BORROWED_2D['1498.fbx'];
    const base = await this.quadEntry(quad.id);
    if (!base?.converted || (base.format ?? 1) < 2 || !base.geometry) return undefined;
    const fitted = fittedToQuad(definition, base.geometry);
    return {
      ...base, ...placedMeasures(fitted, base.geometry),
      id, kind: 'catalog', converted: true, format: base.format, definition: fitted, flatMaterial: quad.material,
      scale: 1, yawQuarters: 0, measured: true, tags: definition.tags,
      variations: definition.variations.map((variation) => ({ id: variation.id,
        slots: variation.slots.map(({ for: name, texture, color, tiling }) => ({ for: name, ...(texture ? { texture } : {}), ...(color ? { color } : {}), ...(tiling ? { tiling } : {}) })) })),
    };
  }

  /**
   * One of the app's quads. They are service models, not published: the queue does not hand them
   * to a guest, so their web bundle, converted once by hand, is read straight off their document.
   */
  private async quadEntry(id: string): Promise<ModelEntry | null> {
    const known = remoteEntries.get(id);
    if (known !== undefined) return known;
    let pending = remotePending.get(id);
    if (!pending) {
      pending = (async () => {
        const url = (await this.documentsOf([id])).get(id)?.bundles?.web;
        if (!url) return null;
        const entry = await this.loadWebEntry(url);
        if (entry && (entry.format ?? 1) >= 2) await this.applyDefinition(id, entry);
        remoteEntries.set(id, entry);
        return entry;
      })().finally(() => remotePending.delete(id));
      remotePending.set(id, pending);
    }
    return pending;
  }

  private async fromCatalog(id: string): Promise<ModelEntry | null> {
    const document = (await this.documentsOf([id]).catch(() => new Map<string, CatalogModel | null>())).get(id);
    if (document && builtByCode(document)) { remoteEntries.set(id, null); return null; }
    if (document?.is2d) {
      const flat = await this.flatFromCatalog(id, document);
      if (flat) { remoteEntries.set(id, flat); return flat; }
    }
    // A `missing` is not settled here: it holds for the project it was asked with (`setCatalogProject`).
    const state = webStateOf(id) ?? (await ensureWebBundles([id]))[id];
    if (state?.status !== 'done' || !state.url) return null;
    const entry = await this.loadWebEntry(state.url);
    if (entry && (entry.format ?? 1) >= 2) await this.applyDefinition(id, entry);
    remoteEntries.set(id, entry);
    return entry;
  }

  /**
   * A format-2 model takes its definition from the catalog when it loads, as the app does: the
   * placed size, bounds and pivot, the variations and tags. The definition is the one the
   * catalog has now, so an edited one shows without a new conversion.
   */
  private async applyDefinition(id: string, entry: ModelEntry): Promise<void> {
    const document = (await this.documentsOf([id])).get(id);
    const url = typeof document?.definition === 'string' ? document.definition : undefined;
    if (!url || !entry.geometry) throw new Error(t('#ViewModelNoDefinition'));
    const xml = await this.definitionXml(url);
    const definition = parseModelDefinition(xml, { width: document!.width, height: document!.height, depth: document!.depth, tags: document!.tags });
    const measures = placedMeasures(definition, entry.geometry);
    Object.assign(entry, measures, {
      definition, scale: 1, yawQuarters: 0 as const, measured: true, tags: definition.tags,
      variations: definition.variations.map((variation) => ({ id: variation.id,
        slots: variation.slots.map(({ for: name, texture, color, tiling }) => ({ for: name, ...(texture ? { texture } : {}), ...(color ? { color } : {}), ...(tiling ? { tiling } : {}) })) })),
    });
  }

  /**
   * A format-2 GLB as the definition places and dresses it: scaled and turned inside a wrapper,
   * so what comes out stands exactly as a format-1 GLB with those baked in did. A slot naming a
   * picture of the model's archive, or a person's upload, takes it; a slot's colour colours a
   * material the picture does not cover. Catalog textures stay for `variationSurfaces`.
   */
  private async shape(gltf: { scene: THREE.Group; parser: { getDependency: (type: string, index: number) => Promise<unknown> } }, entry: ModelEntry): Promise<THREE.Group> {
    const definition = entry.definition!;
    const inner = new THREE.Group();
    inner.scale.set(...definition.scale);
    inner.add(gltf.scene);
    const outer = new THREE.Group();
    outer.rotation.y = yawOf(definition);
    outer.add(inner);
    outer.updateMatrixWorld(true);
    const materials = new Set<THREE.MeshStandardMaterial>();
    gltf.scene.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh) return;
      for (const material of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) {
        if (material instanceof THREE.MeshStandardMaterial) materials.add(material);
      }
    });
    // A 2D item's picture differs from variation to variation: it goes on per instance
    // (`variationSurfaces`), not into the geometry every variation shares.
    if (entry.flatMaterial) return outer;
    await Promise.all([...materials].map(async (material) => {
      const slot = slotFor(definition, material.name);
      if (!slot) return;
      const texture = slot.texture ?? '';
      let map: THREE.Texture | undefined;
      const archived = /\.(jpe?g|png)$/i.test(texture) && !/[\\/]/.test(texture) && !texture.startsWith('[custom]')
        ? pickImage(entry.images ?? {}, texture) : undefined;
      if (archived !== undefined) {
        const source = await gltf.parser.getDependency('texture', archived) as THREE.Texture;
        map = source.clone();
      } else if (texture.startsWith('[custom]')) {
        map = this.textureImages.get(customTextureUrl(texture.slice('[custom]'.length)), { repeat: slot.tiling ?? [1, 1], offset: [0, 0], rotation: 0,
          colorSpace: THREE.SRGBColorSpace, flipY: false, anisotropy: 1 });
      }
      if (map) {
        if (archived !== undefined) {
          map.colorSpace = THREE.SRGBColorSpace;
          map.flipY = false;
          map.wrapS = map.wrapT = THREE.RepeatWrapping;
          if (slot.tiling) map.repeat.set(slot.tiling[0], slot.tiling[1]);
          map.needsUpdate = true;
        }
        material.map = map;
        // The slot of the first variation naming this material: another variation's picture or tiling replaces it (`variationSurfaces`).
        material.userData.definitionSlot = slot;
        // Unity multiplies the colour by the picture; white keeps the picture as it is.
        material.color.set(0xffffff);
      } else if (slot.color) {
        const hex = slot.color.replace('#', '');
        if (hex.length >= 6) material.color.setRGB(...([0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255) as [number, number, number]), THREE.SRGBColorSpace);
      }
      material.needsUpdate = true;
    }));
    return outer;
  }

  /** One round trip: the GLB comes back with its entry inside, and the bytes wait for `source`. */
  private async loadWebEntry(url: string): Promise<ModelEntry | null> {
    return this.request(url, async (signal) => {
      const response = await (this.options.fetch ?? fetch)(url, { signal });
      if (response.status === 404) return null;
      if (!response.ok) throw new Error(t('#ViewModelHttpError', response.status));
      const data = await response.arrayBuffer();
      const entry = entryFromGlb(data);
      // Anything that is not a converted GLB — an error page, a stub in a test — means the
      // model is unavailable, not that the scene failed.
      if (!entry?.converted) return null;
      entry.url = url;
      entry.kind = 'catalog';
      remoteBuffers.set(url, data);
      return entry;
    }, undefined, REMOTE_TIMEOUT_MS);
  }

  /**
   * Ask the catalog about everything a project references before it is parsed: models
   * with a web bundle are fetched in parallel (the parse needs each one's real size, pivot
   * and tags); the rest are put in the queue and arrive later through `subscribeWebStates`.
   * The project opens without waiting for them: their items stand as placeholders.
   */
  async prepare(ids: Iterable<string>, onProgress?: (done: number, total: number) => void, concurrency = 12): Promise<{ total: number; missing: string[]; pending: string[] }> {
    const index = await this.manifest();
    const missing: string[] = [], pending: string[] = [];
    if (this.manifestUrl) return { total: 0, missing, pending };
    const unique = [...new Set(ids)];
    // Another library of this tab fetched it already: its entry is this one's too, as `resolve` takes it, or the parse misses its size and pivot.
    for (const id of unique) { const shared = remoteEntries.get(id); if (shared && !index[id]?.converted) index[id] = shared; }
    const todo = unique.filter((id) => !index[id]?.converted && remoteEntries.get(id) === undefined && catalogKeyOf(id));
    if (!todo.length) return { total: 0, missing, pending };
    // 2D items borrow the app's own quads and are not converted: they skip the queue. What the app builds by code — a
    // bare opening, a wall of its own — has no model at all: it is settled here and never asked of the queue.
    const found = await this.documentsOf(todo).catch(() => new Map<string, CatalogModel | null>());
    const byCode = (id: string) => { const document = found.get(id); return !!document && !!builtByCode(document); };
    for (const id of todo) if (byCode(id)) remoteEntries.set(id, null);
    const flat = todo.filter((id) => !byCode(id) && found.get(id)?.is2d);
    const converted = todo.filter((id) => !byCode(id) && !found.get(id)?.is2d);
    const states = converted.length ? await ensureWebBundles(converted) : {};
    const ready = [...flat, ...converted.filter((id) => states[id]?.status === 'done')];
    for (const id of converted) {
      const status = states[id]?.status;
      if (status === 'queued' || status === 'running') pending.push(id);
      else if (status !== 'done') missing.push(id);
    }
    let done = 0;
    const queue = ready.slice();
    const worker = async (): Promise<void> => {
      for (let id = queue.shift(); id !== undefined; id = queue.shift()) {
        try {
          if (!(await this.resolve(id))?.converted) {
            // A 2D item whose quad is not converted yet went through the queue after all.
            const status = webStateOf(id)?.status;
            (status === 'queued' || status === 'running' ? pending : missing).push(id);
          }
        } catch { missing.push(id); }
        onProgress?.(++done, ready.length);
      }
    };
    await Promise.all(Array.from({ length: Math.min(concurrency, ready.length) }, worker));
    return { total: todo.length, missing, pending };
  }

  /**
   * The stock look of a model converted on demand: its bundle carries shader parameters,
   * the catalog's variation names the image. Applied under the project's own overrides,
   * for the materials the GLB left untextured — as the app dresses a downloaded model — and
   * for those the definition dressed with another variation's picture or tiling (`shape`).
   */
  private async variationSurfaces(entry: ModelEntry, variation: ModelEntry['variations'][number], node: THREE.Object3D): Promise<Record<string, Surface>> {
    if (!variation.slots.length) return {};
    if (entry.flatMaterial) {
      // A 2D item: the variation's picture or colour on its quad's picture material.
      const slot = variation.slots.find((value) => value.texture || value.color);
      const texture = slot?.texture ?? '';
      if (texture.startsWith('[custom]')) return { [entry.flatMaterial]: { textureKind: 'custom', customTexture: texture.slice('[custom]'.length), tiling: [1, 1] } };
      const surface = slot ? stockSurfaceFor(slot, await textureIndex(), entry) : null;
      return surface ? { [entry.flatMaterial]: surface } : {};
    }
    // A material's picture by its key: the definition's slot when `shape` dressed it, undefined when the GLB's own.
    const worn = new Map<string, DefinitionSlot | undefined>();
    node.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      for (const mat of Array.isArray(m.material) ? m.material : [m.material]) {
        if ((mat as THREE.MeshStandardMaterial).map) worn.set(normalizeKey(mat.name), mat.userData.definitionSlot as DefinitionSlot | undefined);
      }
    });
    const wanted = variation.slots.filter((slot) => {
      if (!slot.for) return false;
      const key = normalizeKey(slot.for);
      if (!worn.has(key)) return true;
      const dressed = worn.get(key);
      return dressed !== undefined && ((dressed.texture ?? '') !== (slot.texture ?? '') || !sameTiling(dressed.tiling, slot.tiling));
    });
    // The catalog's recipe for the slot's texture (a short name or a library path) first; the
    // staged index is what remains for the owner's machine until every texture is published.
    const [index, published] = await Promise.all([textureIndex(), builtinSurfaces(wanted.map(slot => slot.texture ?? '').filter(Boolean))]);
    const out: Record<string, Surface> = {};
    for (const slot of wanted) {
      const surface = (slot.texture && published.get(slot.texture)) || definitionPicture(slot, entry) || stockSurfaceFor(slot, index, entry);
      if (surface) out[slot.for] = surface;
    }
    return out;
  }

  /** Image I/O for the library-owned configured texture cache. */
  get textures(): THREE.TextureLoader { return this.textureLoader; }

  /**
   * Reuse ready GPU textures when copying furniture. TextureLoader itself returns a
   * fresh, initially empty texture even when the browser already cached its image.
   * Include the complete sampler configuration: editing a copy's tiling or offset
   * must never mutate the original's map. A picture that failed is asked for again later, not
   * by every dressing: by a use after its pause (`retryPause`) or at once by `retryFailed`.
   */
  overrideTexture(url: string, surface: Surface, maxAnisotropy: number, explicitTiling: boolean, normal = false): THREE.Texture {
    if (this.disposed) throw new Error(t('#ViewLoadCanceled'));
    const repeat = drawRepeat(surface, explicitTiling), offset = surface.offset ?? [0, 0];
    const colorSpace = normal ? THREE.NoColorSpace : THREE.SRGBColorSpace;
    const texture = this.textureImages.get(url, { repeat, offset, rotation: surface.rotation, colorSpace, flipY: false, anisotropy: maxAnisotropy });
    return texture;
  }

  embeddedTexture(source: THREE.Texture, surface: Surface, maxAnisotropy: number, explicitTiling: boolean, reference = source): THREE.Texture {
    const desired = surface.tileMeters || surface.tiling ? drawRepeat(surface, explicitTiling) : reference.repeat.toArray();
    const ratioX = Math.abs(reference.repeat.x) > 1e-8 ? desired[0] / reference.repeat.x : 1;
    const ratioY = Math.abs(reference.repeat.y) > 1e-8 ? desired[1] / reference.repeat.y : 1;
    const rotationDelta = surface.rotation === undefined ? 0 : surface.rotation + reference.rotation * 180 / Math.PI;
    const texture = this.textureImages.fromTexture(source, { repeat: [source.repeat.x * ratioX, source.repeat.y * ratioY],
      offset: surface.offset ? [source.offset.x + surface.offset[0] - reference.offset.x, source.offset.y + surface.offset[1] - reference.offset.y] : source.offset.toArray(),
      rotation: -source.rotation * 180 / Math.PI + rotationDelta,
      colorSpace: source.colorSpace, flipY: source.flipY, anisotropy: maxAnisotropy });
    return texture;
  }

  /**
   * A picture of a model's own archive by the name a project gives it: an item's material in
   * the project file says `TexturePath="393_Wood_Floor.jpg"`, bare, and the app looks that name
   * up among the model's own pictures. A format-2 GLB carries every picture of the archive under
   * its name, but the loader keeps only those the definition dresses the model with, so the file
   * is read again (the browser answers from its cache or with a 304) and the one picture comes
   * out as an object URL. Null when the model carries no such picture.
   */
  async archivePicture(entry: ModelEntry, name: string): Promise<string | null> {
    const index = (entry.format ?? 1) >= 2 && entry.images && entry.url ? pickImage(entry.images, name) : undefined;
    if (index === undefined || this.disposed) return null;
    const key = `${entry.url}\n${index}`;
    let picture = this.archivePictures.get(key);
    if (!picture) {
      picture = this.request(entry.url, async (signal) => {
        const response = await (this.options.fetch ?? fetch)(entry.url, { signal });
        if (!response.ok) throw new Error(t('#ViewModelHttpError', response.status));
        const found = glbPicture(await response.arrayBuffer(), index);
        return found ? URL.createObjectURL(new Blob([found.bytes as BlobPart], { type: found.mimeType })) : null;
      }, (url) => { if (url) URL.revokeObjectURL(url); }, REMOTE_TIMEOUT_MS).catch(() => {
        // A failed read is not remembered: the next instance asks again.
        if (this.archivePictures.get(key) === picture) this.archivePictures.delete(key);
        return null;
      });
      this.archivePictures.set(key, picture);
    }
    return picture;
  }

  /** The archive pictures these surfaces name, found in the GLB of the model they dress. */
  async ownPictures(surfaces: Record<string, Surface> | null | undefined, entry: ModelEntry): Promise<Map<Surface, string>> {
    const out = new Map<Surface, string>();
    await Promise.all(Object.values(surfaces ?? {}).map(async (surface) => {
      if (surface.textureKind !== 'modelOwn' || !surface.texture) return;
      const url = await this.archivePicture(entry, surface.texture);
      if (url) out.set(surface, url);
    }));
    return out;
  }

  /**
   * The own surfaces of an item, an opening or a part as this site can draw them. The app writes a picture of a
   * built-in model's archive as its path under Resources/Models, which no server here serves: the model's GLB gives
   * that picture by its name, else a slot its variation dresses (`dressed`, worn by the GLB already or not) keeps
   * the look the variation gives it.
   */
  async reachableOwn(own: Record<string, Surface>, dressed: string[], entry: ModelEntry, modelId: string): Promise<Record<string, Surface> | null> {
    const folder = modelId.slice(0, modelId.lastIndexOf('/') + 1);
    const dressedKeys = new Set(dressed.map(normalizeKey));
    const out: Record<string, Surface> = {};
    for (const [key, surface] of Object.entries(own)) {
      if (surface.textureKind !== 'model' || !folder || !surface.texture?.startsWith(folder)) { out[key] = surface; continue; }
      const name = surface.texture.slice(surface.texture.lastIndexOf('/') + 1);
      // A 2D item's GLB is the quad it borrows, another model's archive.
      if (!entry.flatMaterial && await this.archivePicture(entry, name)) out[key] = { ...surface, textureKind: 'modelOwn', textureOwner: entry.id, texture: name };
      else if (!dressedKeys.has(normalizeKey(key))) out[key] = surface;
    }
    return Object.keys(out).length ? out : null;
  }

  /** What an item or an opening wears, as the app's `TextureLoader.LoadTexture`; under `'*'` the paint alone, which a stock surface would beat. */
  async dressing(entry: ModelEntry, subject: Pick<Item, 'modelRef' | 'materials' | 'variationId'>, node: THREE.Object3D): Promise<Record<string, Surface> | null> {
    const { materials } = subject;
    const variation = entry.kind && !materials?.['*'] ? entry.variations?.find((v) => v.id === subject.variationId) ?? entry.variations?.[0] : undefined;
    const stock = variation ? await this.variationSurfaces(entry, variation, node) : {};
    const dressed = entry.flatMaterial ? Object.keys(stock) : variation?.slots.flatMap((slot) => slot.for ? [slot.for] : []) ?? [];
    const own = materials && Object.keys(materials).length ? await this.reachableOwn(materials, dressed, entry, subject.modelRef.id) : null;
    return Object.keys(stock).length ? { ...stock, ...(own ?? {}) } : own;
  }

  /** Effective visible slots, including native embedded defaults absent from Scene IR. */
  materialSlots(node: THREE.Object3D, item: Pick<Item, 'modelRef'>): MaterialSlotInfo[] {
    const slots = new Map<string, MaterialSlotInfo>(), entry = this.entry(item.modelRef.id);
    node.traverse(object => {
      if (!(object instanceof THREE.Mesh)) return;
      const materials = Array.isArray(object.material) ? object.material : [object.material];
      for (const [materialIndex, material] of materials.entries()) {
        if (!(material instanceof THREE.MeshStandardMaterial)) continue;
        const key = material.name || '*', id = normalizeKey(key), previous = slots.get(id);
        const groups = (object.geometry as THREE.BufferGeometry).groups.filter(group => group.materialIndex === materialIndex);
        const weight = groups.length ? groups.reduce((sum, group) => sum + group.count, 0)
          : (object.geometry.index?.count ?? object.geometry.getAttribute('position')?.count ?? 0) / materials.length;
        if (previous) { previous.weight += weight; continue; }
        const description = describeMaterial(material);
        const native = entry?.variations?.[0]?.slots.find(slot => normalizeKey(slot.for) === id)?.texture;
        slots.set(id, { key, label: FACE_LABELS[id] ? t(FACE_LABELS[id]) : key.replace(/^vray[_\s]*/i, '').replace(/[_-]/g, ' '), ...description,
          textureName: material.userData.hpSurface?.texture ?? material.userData.hpSurface?.customTexture ?? native ?? description.textureName, weight });
      }
    });
    return [...slots.values()].sort((a, b) => b.weight - a.weight);
  }

  /** The model's geometry, cached and shared; callers clone it. */
  async geometry(entry: ModelEntry): Promise<THREE.Group | null> { return this.source(entry); }

  async acquireGeometry(entry: ModelEntry): Promise<{ source: THREE.Group | null; release: () => void }> {
    const promise = this.source(entry), key = this.sourceKeys.get(entry.id)!;
    const release = this.resources.pin(key);
    try { return { source: await promise, release }; }
    catch (error) { release(); this.trimUnused(); throw error; }
  }

  ownInstance(root: THREE.Object3D): THREE.Object3D {
    root.traverse(object => {
      if (!(object instanceof THREE.Mesh)) return;
      if (!this.resources.has(object.geometry)) this.resources.register(object.geometry, [object.geometry]);
      const intern = (material: THREE.Material) => {
        if (this.resources.has(material)) return material;
        const key = materialKey(material), cached = this.materials.get(key);
        if (cached) { material.dispose(); return cached; }
        this.materials.set(key, material);
        this.resources.register(material, [material], () => { if (this.materials.get(key) === material) this.materials.delete(key); });
        return material;
      };
      object.material = Array.isArray(object.material) ? object.material.map(intern) : intern(object.material);
    });
    this.leases.set(root, this.retainResources(root));
    return root;
  }

  /**
   * Build an item that has no mesh of its own out of its module tree.
   *
   * The assembled bounds — not the catalog dimensions — are what the project's `Scale`
   * multiplies, so the whole assembly is fitted to `item.size` afterwards. Cabinet depth
   * routinely exceeds the catalog figure because doors and handles protrude, and that
   * larger depth is what keeps the cabinet's back flush against the wall.
   */
  private async assembled(item: Item, maxAnisotropy: number): Promise<THREE.Object3D | null> {
    const parts = flattenModules(item);
    if (!parts.length) return null;
    const results = await Promise.allSettled(parts.map((p) => buildPart(this, p, maxAnisotropy)));
    const built = results.flatMap((result) => result.status === 'fulfilled' && result.value ? [result.value] : []);
    const failure = results.find((result) => result.status === 'rejected');
    if (failure?.status === 'rejected') {
      for (const node of built) this.release(node);
      throw failure.reason;
    }
    if (!built.length) return null;

    const assembly = new THREE.Group();
    for (const b of built) assembly.add(b);

    const box = new THREE.Box3().setFromObject(assembly);
    const span = box.getSize(new THREE.Vector3());
    const centre = box.getCenter(new THREE.Vector3());
    // Re-origin: centred in X and Z, standing on Y = 0, matching how a single model sits.
    assembly.position.set(-centre.x, -box.min.y, -centre.z);

    const holder = new THREE.Group();
    holder.add(assembly);
    // A modular item's catalog size IS its assembled size, so the project's Scale applies
    // to that directly. Fitting to the XML's Width/Height/Depth instead would squash the
    // cabinet's depth, because the XML figure excludes the doors that stand proud of it.
    const [ax, ay, az] = item.scale;
    holder.scale.set(ax, Math.abs(ay), Math.abs(az));
    if (item.mirrored) {
      holder.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.isMesh) return;
        const mats = Array.isArray(m.material) ? m.material : [m.material];
        const owned = mats.map((mat) => {
          const own = this.resources.has(mat) ? mat.clone() : mat;
          own.side = THREE.DoubleSide;
          return own;
        });
        m.material = Array.isArray(m.material) ? owned : owned[0];
      });
    }

    const root = new THREE.Group();
    root.add(holder);
    applyShadowPolicy(holder);
    if (item.placement === 'ceiling') holder.traverse((object) => { if (object instanceof THREE.Mesh) object.castShadow = false; });
    root.position.set(item.position.x, item.elevation, item.position.z);
    root.rotation.y = -item.rotation;
    root.name = `item:${item.id}`;
    root.userData = {
      kind: 'item', itemId: item.id, caption: item.caption,
      model: item.modelRef.raw, real: true, parts: built.length,
    };
    return this.ownInstance(root);
  }

  /** The model's geometry, cached and shared; callers clone it. */
  private async source(entry: ModelEntry): Promise<THREE.Group | null> {
    let p = this.cache.get(entry.id);
    if (!p) {
      const key = {}, forget = () => {
        if (this.sourceKeys.get(entry.id) !== key) return;
        this.sourceKeys.delete(entry.id); this.cache.delete(entry.id);
        if (source) this.sources.delete(source);
      };
      let source: THREE.Group | undefined;
      this.sourceKeys.set(entry.id, key);
      this.resources.register(key, [], forget, Math.max(1, entry.bytes));
      p = this.request(entry.url, async (signal) => {
        if (this.options.loadModel) return this.options.loadModel(entry, signal);
        let data = remoteBuffers.get(entry.url);
        if (data) remoteBuffers.delete(entry.url);
        else {
          const response = await (this.options.fetch ?? fetch)(entry.url, { signal, cache: 'no-cache' });
          if (!response.ok) throw new Error(t('#ViewModelHttpError', response.status));
          data = await response.arrayBuffer();
        }
        const url = new URL(entry.url, typeof location === 'undefined' ? 'http://localhost/' : location.href);
        const path = new URL('.', url).href;
        const gltf = await this.loader.parseAsync(data, path);
        return (entry.format ?? 1) >= 2 && entry.definition ? this.shape(gltf as never, entry) : gltf.scene;
      }, (node) => this.resources.disposeLoose(resourcesOf(node))).then((node) => {
        sanitizeSourceMaterials(node);
        source = node;
        this.sources.add(node);
        this.resources.register(key, resourcesOf(node), forget);
        return node;
      }).catch((error) => {
        if (this.cache.get(entry.id) === p) this.cache.delete(entry.id);
        this.resources.remove(key);
        throw error;
      });
      this.cache.set(entry.id, p);
    }
    return p;
  }

  /**
   * A placed instance of `item`, or null when nothing can be built.
   * The returned object is positioned, rotated and scaled in world space.
   */
  async instance(item: Item, maxAnisotropy: number): Promise<THREE.Object3D | null> {
    const entry = await this.resolve(item.modelRef.id);
    // An item whose own model carries no mesh is assembled from its parts instead.
    if (!entry?.converted && item.modules?.length) return this.assembled(item, maxAnisotropy);
    if (!entry?.converted) return null;
    const acquired = await this.acquireGeometry(entry), src = acquired.source;
    if (!src) { acquired.release(); return null; }
    try {
    const node = src.clone(true);
    // Materials are cloned ONLY when this instance overrides them. Cloning
    // unconditionally gave every copy of a chair its own material: 500+ distinct
    // materials for 100 items, and the renderer loses every batching opportunity.
    const overrides = await this.dressing(entry, item, node);
    if (overrides || item.mirrored) {
      node.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.isMesh) return;
        m.material = Array.isArray(m.material)
          ? m.material.map(cloneOverrideMaterial)
          : cloneOverrideMaterial(m.material as THREE.Material);
      });
      if (overrides) {
        const [, pictures] = await Promise.all([publishedTextures(overrides), this.ownPictures(overrides, entry)]);
        applyOverrides(node, overrides, this, maxAnisotropy, !!item.usesExplicitTiling, pictures);
      }
    }
    applyShadowPolicy(node);
    if (item.placement === 'ceiling') node.traverse((object) => { if (object instanceof THREE.Mesh) object.castShadow = false; });

    // Catalog dims × the project's per-axis Scale is already baked into item.size,
    // so the per-axis factor is item.size / the catalog size the GLB normalises to.
    const axis = (want: number, have: number): number => (have > 1e-6 ? want / have : 1);
    // The model stands as its definition turns it (`shape`), or as an older bundle authored
    // across its stated width is turned back; the item's size and mirroring go on a group
    // round it, so they act along the item's axes however the model is turned
    // (`fitScale`, which the stairwells share).
    if (entry.yawQuarters) node.rotation.y = Math.PI / 2;
    const fit = new THREE.Group();
    fit.add(node);
    fit.scale.set(...fitScale(entry, item));
    if (item.mirrored) {
      // A negative scale inverts winding; render both sides rather than showing holes.
      node.traverse((o) => {
        const m = o as THREE.Mesh;
        if (m.isMesh) {
          const mats = Array.isArray(m.material) ? m.material : [m.material];
          for (const mat of mats) (mat as THREE.MeshStandardMaterial).side = THREE.DoubleSide;
        }
      });
    }

    // Sit the mesh's real bottom on `elevation` rather than trusting the origin.
    const bottom = entry.bounds.min[1] * axis(item.size.h, entry.size[1]);
    fit.position.y = -bottom;

    const holder = new THREE.Group();
    holder.add(fit);
    holder.position.set(item.position.x, item.elevation, item.position.z);
    // See the note in buildScene.placeholderFor: the IR's yaw is the reference
    // implementation's, whose forward is (−sin r, cos r); three.js needs it negated.
    holder.rotation.y = -item.rotation;
    holder.name = `item:${item.id}`;
    holder.userData = { kind: 'item', itemId: item.id, caption: item.caption, model: item.modelRef.raw, real: true };
    return this.ownInstance(holder);
    } finally { acquired.release(); }
  }
}

/**
 * Project material overrides are keyed by the FBX material name.
 *
 * Unity matches those keys ordinal case-insensitively and strips a trailing
 * " (Instance)". `*` is the wildcard; `_auto_` is NOT — it is a real Unity material name,
 * so treating it as a catch-all repaints every slot of any item that overrides it.
 */
/**
 * The catalog's answer for every app texture these surfaces name, before they are applied: a
 * texture resolves to the picture the catalog publishes only once its answer is known, and a
 * model dressed before that keeps its own look on the site, or in development asks the owner-only
 * `/hp/tex` path and stays black where it is absent.
 */
async function publishedTextures(surfaces: Record<string, Surface> | null | undefined): Promise<void> {
  const refs = Object.values(surfaces ?? {}).filter((s) => s.textureKind === 'catalog' && s.texture).map((s) => s.texture!);
  if (refs.length) await builtinSurfaces(refs);
}

/**
 * `pictures` holds what `ModelLibrary.ownPictures` found for the surfaces naming a picture of
 * the model's own archive. Such a surface whose picture is not there keeps the picture the model
 * already wears (a coloured surface without one takes its colour), as nothing on the server
 * serves those pictures separately.
 */
function applyOverrides(
  node: THREE.Object3D,
  overrides: Record<string, Surface>,
  library: ModelLibrary,
  maxAnisotropy: number,
  explicitTiling: boolean,
  pictures?: ReadonlyMap<Surface, string>,
): void {
  const byKey = new Map<string, Surface>();
  for (const [k, v] of Object.entries(overrides)) byKey.set(normalizeKey(k), v);
  const wildcard = overrides['*'];

  node.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh) return;
    const mats = (Array.isArray(m.material) ? m.material : [m.material]) as THREE.MeshStandardMaterial[];
    for (const mat of mats) {
      const s = byKey.get(normalizeKey(mat.name)) ?? wildcard;
      if (!s) continue;
      const transformOnly = !s.texture && !s.customTexture && !s.color && !s.type;
      if (transformOnly) {
        const reference = mat.map;
        // Preserve embedded alpha/roughness/emission and their authored relative UVs.
        for (const [key, value] of Object.entries(mat)) {
          if (!(value instanceof THREE.Texture) || value.mapping !== THREE.UVMapping) continue;
          (mat as unknown as Record<string, unknown>)[key] = library.embeddedTexture(value, s, maxAnisotropy, explicitTiling, reference ?? value);
        }
        bindMaterialSurface(mat, s, explicitTiling); mat.needsUpdate = true;
        continue;
      }
      const own = s.textureKind === 'modelOwn', picture = own ? pictures?.get(s) : undefined;
      const tex = own ? (picture ? { url: picture, repeat: repeatOf(s, explicitTiling) } : null) : resolveTexture(s, explicitTiling);
      // An own picture the model's GLB lacks: the model keeps its maps, as when nothing is named.
      const keepsLook = own && !picture;
      // The picture's own name tells a PNG: an archive picture's URL is an object URL.
      const pictureName = picture ? s.texture ?? '' : tex?.url ?? '';
      if (tex) {
        mat.map = library.overrideTexture(tex.url, s, maxAnisotropy, explicitTiling);
        mat.color.set(0xffffff);
      } else if (s.color && !(keepsLook && mat.map)) {
        mat.map = null;
        mat.color.setRGB(s.color[0], s.color[1], s.color[2], THREE.SRGBColorSpace);
      } else if (mat.map && (s.tileMeters || s.tiling || s.offset || s.rotation !== undefined)) {
        mat.map = library.embeddedTexture(mat.map, s, maxAnisotropy, explicitTiling);
      }
      mat.roughness = roughnessOf(s, mat.roughness);
      if (mat instanceof THREE.MeshPhysicalMaterial) {
        mat.specularColor.setScalar(specularOf(s));
        mat.clearcoat = 0;
      }
      if (s.type) mat.metalness = metalnessOf(s);
      const emission = emissiveOf(s);
      mat.emissive.copy(mat.color);
      mat.emissiveIntensity = emission;
      mat.emissiveMap = emission > 0 ? mat.map : null;
      const normalUrl = normalTextureOf(s);
      if (normalUrl && mat.map) {
        mat.normalMap = library.overrideTexture(normalUrl, s, maxAnisotropy, explicitTiling, true);
        mat.normalScale.set(.65, .65);
      } else if ((s.texture || s.color) && !keepsLook) mat.normalMap = null;
      else if (mat.normalMap && (s.tileMeters || s.tiling || s.offset || s.rotation !== undefined)) {
        mat.normalMap = library.embeddedTexture(mat.normalMap, s, maxAnisotropy, explicitTiling);
      }
      const opacity = opacityOf(s);
      if (opacity !== undefined) { mat.transparent = opacity < 1; mat.opacity = opacity; }
      else if (s.type) { mat.transparent = false; mat.opacity = 1; }
      // Lite ObjModelLoader.material uses the PNG alpha mask for wicker and mesh.
      // Alpha testing keeps those holes crisp and writes depth only for solid fibres.
      mat.alphaTest = tex && /\.png(?:$|\?)/i.test(pictureName) && !mat.transparent ? .5 : 0;
      mat.depthWrite = !mat.transparent;
      if (mat.transparent || mat.alphaTest > 0) mat.side = THREE.DoubleSide;
      bindMaterialSurface(mat, s, explicitTiling);
      mat.needsUpdate = true;
    }
  });
}

function normalizeKey(name: string): string {
  return name.replace(/\s*\(Instance\)\s*$/i, '').trim().toLowerCase();
}

/** Fitted geometry for one part of a modular item. */
async function buildPart(
  lib: ModelLibrary,
  part: { modelId: string; matrix: THREE.Matrix4; materials: Record<string, Surface>; slot: string },
  maxAnisotropy: number,
): Promise<THREE.Object3D | null> {
  const entry = await lib.resolve(part.modelId);
  if (!entry?.converted) return null;
  const acquired = await lib.acquireGeometry(entry), src = acquired.source;
  if (!src) { acquired.release(); return null; }
  try {
  const node = src.clone(true);
  const partOverrides = Object.keys(part.materials).length ? await lib.reachableOwn(part.materials, [], entry, part.modelId) : null;
  if (partOverrides) {
    node.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      m.material = Array.isArray(m.material)
        ? m.material.map(cloneOverrideMaterial)
        : cloneOverrideMaterial(m.material as THREE.Material);
    });
    const [, pictures] = await Promise.all([publishedTextures(partOverrides), lib.ownPictures(partOverrides, entry)]);
    applyOverrides(node, partOverrides, lib, maxAnisotropy, false, pictures);
  }
  applyShadowPolicy(node);

  // Each part is authored at its own catalog size; only the uniform normalisation applies.
  node.scale.setScalar(entry.scale);
  // Parts are authored centred in X and standing on Y=0, with Z left as authored —
  // the layout offsets are computed against that convention.
  node.position.set(
    -((entry.bounds.min[0] + entry.bounds.max[0]) / 2),
    -entry.bounds.min[1],
    0,
  );

  const holder = new THREE.Group();
  holder.add(node);
  holder.applyMatrix4(part.matrix);
  holder.userData = { part: part.modelId, slot: part.slot };
  return lib.ownInstance(holder);
  } finally { acquired.release(); }
}

/**
 * Only objects big enough to cast a visible shadow do so.
 *
 * The shadow map is a second full pass over the scene: with every vase and handle casting,
 * the frame costs twice what it needs to and the extra shadows are sub-pixel anyway.
 */
const SHADOW_MIN_SIZE = 0.32;

function applyShadowPolicy(node: THREE.Object3D): void {
  node.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh) return;
    m.receiveShadow = true;
    if (!m.geometry.boundingBox) m.geometry.computeBoundingBox();
    const b = m.geometry.boundingBox;
    if (!b) { m.castShadow = false; return; }
    const s = b.getSize(new THREE.Vector3()).multiply(worldScale(m));
    // Two dimensions must be substantial: a tall thin lamp post casts nothing useful.
    const dims = [s.x, s.y, s.z].sort((a, b2) => b2 - a);
    m.castShadow = dims[1] >= SHADOW_MIN_SIZE;
  });
}

function worldScale(o: THREE.Object3D): THREE.Vector3 {
  const s = new THREE.Vector3(1, 1, 1);
  let cur: THREE.Object3D | null = o;
  while (cur) { s.multiply(cur.scale); cur = cur.parent; }
  return s.set(Math.abs(s.x), Math.abs(s.y), Math.abs(s.z));
}

/** A primitive's faces are its material slots (a box's six, a mansard's five): named for the inspector. */
const FACE_LABELS: Record<string, TranslationKey> = { top: '#ViewFaceTop', bottom: '#ViewFaceBottom', front: '#ViewFaceFront', back: '#ViewFaceBack', left: '#ViewFaceLeft', right: '#ViewFaceRight', slope: '#ViewFaceSlope' };

/**
 * The door or window that fills an opening.
 *
 * Openings are cut as holes and given reveals, but the leaf itself is a catalog model like
 * any other — without it a glazed wall reads as a missing wall rather than as glass.
 * It is fitted to the aperture rather than to its own catalog size, because the user sized
 * the opening, not the model.
 */
export async function buildOpeningModel(
  lib: ModelLibrary,
  opening: Opening,
  frame: { center: { x: number; z: number }; centerY: number; width: number; height: number; yaw: number; thickness: number },
  maxAnisotropy: number,
): Promise<THREE.Object3D | null> {
  if (opening.design?.style) {
    const procedural = buildProceduralOpening(opening, frame);
    return procedural ? lib.ownInstance(procedural) : null;
  }
  if (opening.modelRef.kind === 'none' || !opening.modelRef.id) return null;
  const entry = await lib.resolve(opening.modelRef.id);
  if (!entry?.converted) return null;
  const acquired = await lib.acquireGeometry(entry), src = acquired.source;
  if (!src) { acquired.release(); return null; }
  try {
  const node = src.clone(true);
  // The viewer lists the parts of this model, not of the curtains beside it, for the opening's colours.
  node.userData.openingModel = true;
  const overrides = await lib.dressing(entry, opening, node);
  if (overrides) {
    node.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      m.material = Array.isArray(m.material)
        ? m.material.map(cloneOverrideMaterial)
        : cloneOverrideMaterial(m.material as THREE.Material);
    });
    const [, pictures] = await Promise.all([publishedTextures(overrides), lib.ownPictures(overrides, entry)]);
    applyOverrides(node, overrides, lib, maxAnisotropy, true, pictures);
  }
  applyShadowPolicy(node);

  const axis = (want: number, have: number) => (have > 1e-6 ? want / have : 1);
  // On a group round the model, as an item's size (`instance`): along the wall and up it however
  // the definition turns the model.
  const fit = new THREE.Group(); fit.add(node);
  fit.scale.set(
    entry.scale * axis(opening.width, entry.size[0]),
    entry.scale * axis(opening.height, entry.size[1]),
    entry.scale,
  );
  fit.position.y = -entry.bounds.min[1] * axis(opening.height, entry.size[1]);

  const holder = openingAssembly(opening, frame);
  const orientation = new THREE.Group(); holder.add(orientation); orientation.add(fit);
  orientation.position.z = frame.thickness / 2 - openingDesign(opening, frame.thickness).recess;
  // Native opening Rotation=180 changes both the hinge side and the facing side.
  orientation.rotation.y = opening.flipped ? Math.PI : 0;
  addOpeningCurtains(holder, opening, frame);
  return lib.ownInstance(holder);
  } finally { acquired.release(); }
}

function materialKey(material: THREE.Material): string {
  const encode = (value: unknown): unknown => {
    if (value instanceof THREE.Texture) return ['texture', value.uuid];
    if (typeof value === 'function') return value.toString();
    if (!value || typeof value !== 'object') return value;
    if (Array.isArray(value)) return value.map(encode);
    return Object.fromEntries(Object.entries(value).sort(([a], [b]) => a.localeCompare(b)).map(([key, entry]) => [key, encode(entry)]));
  };
  return JSON.stringify(encode(Object.fromEntries(Object.entries(material).filter(([key]) => !['uuid', 'id', 'version', '_listeners'].includes(key)))));
}

/** Invalid Assimp smoothness conversion is not a deliberate polished finish. */
function sanitizeSourceMaterials(node: THREE.Object3D): void {
  node.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    const materials = Array.isArray(object.material) ? object.material : [object.material];
    for (const material of materials) {
      if (!(material instanceof THREE.MeshStandardMaterial)) continue;
      if (!Number.isFinite(material.roughness) || material.roughness < 0 || material.roughness > 1) material.roughness = .65;
      material.metalness = Number.isFinite(material.metalness) ? THREE.MathUtils.clamp(material.metalness, 0, 1) : 0;
      if (material.normalMap) material.normalMap.colorSpace = THREE.NoColorSpace;
    }
  });
}

function cloneOverrideMaterial(source: THREE.Material): THREE.Material {
  if (!(source instanceof THREE.MeshStandardMaterial) || source instanceof THREE.MeshPhysicalMaterial) return source.clone();
  const material = new THREE.MeshPhysicalMaterial();
  THREE.MeshStandardMaterial.prototype.copy.call(material, source);
  return material;
}
